/**
 * @file 对局桌 16:9 布局坐标（基准 1280×720，运行时可整体缩放）。
 *
 * 坐标**唯一基准**为美术规格 `design/gdd/08-ui-visual-spec.md` §3.2 区域坐标总表
 * 与 §4 各区域详细规格（E2：1280×720 唯一坐标系）。修改本文件即修改整张桌的
 * 视觉基准，任何「按当前分辨率直接写像素」都禁止（E2）。
 *
 * 约定（viewer 视角，viewerSeat = 自家 = 下家）：
 * - 自家（seat 0 / viewer）：下方横排手牌（正面），x∈[396,884] y=600；
 * - 对家（seat 2）：上方横排手牌（背面），x∈[484,796] y=58；
 * - 右家（seat 1）：右侧竖排手牌（背面），x∈[1222,1260] y∈[180,558]；
 * - 左家（seat 3）：左侧竖排手牌（背面），x∈[20,58] y∈[180,558]；
 * - 中央弃牌河四边带（§4.3）：上带=对家 / 下带=自家 / 左带=左家 / 右带=右家；
 * - 操作按钮区右下角（§4.5）：碰/杠/胡竖排 x=1000，PASS 与胡同行右侧 x=1144。
 */

import type { SeatIndex } from '@majiang/core';
import { TILE_W } from './tileArt';

/** 基准画布尺寸（16:9，§2.1）。 */
export const BASE_W = 1280;
export const BASE_H = 720;

/* ─────────────────────────── 区域坐标总表（§3.2） ─────────────────────────── */

export const AREA = {
  /** 顶部 HUD：返回/标题居左、牌墙剩余+设置居右。 */
  hud: { x: 16, y: 8, w: 1248, h: 48 },
  /** 对家区。 */
  top: { x: 430, y: 58, w: 420, h: 134 },
  /** 左家区。 */
  left: { x: 16, y: 180, w: 128, h: 460 },
  /** 右家区（镜像）。 */
  right: { x: 1136, y: 180, w: 128, h: 460 },
  /** 中央弃牌河（四边带 + 中央信息区）。 */
  river: { x: 304, y: 252, w: 672, h: 254 },
  /** 自家副露（横排，自适应缩放）。 */
  selfMeld: { x: 896, y: 480, w: 344, h: 80 },
  /** 操作按钮区（右下角）。 */
  action: { x: 1000, y: 560, w: 264, h: 140 },
  /** 自家手牌区。 */
  selfHand: { x: 396, y: 600, w: 488, h: 64 },
  /** 结算面板（覆盖层）。 */
  settle: { x: 240, y: 80, w: 800, h: 560 },
} as const;

/* ─────────────────────────── 牌尺寸与缩放（§6.1） ─────────────────────────── */

/** 各档牌显示尺寸（px，§6.1 / §4 明细）。 */
export const TILE_SPEC = {
  /** 自家手牌（正面，§4.6）。 */
  hand: { w: 46, h: 64 },
  /** 弃牌河（§4.3）。 */
  river: { w: 30, h: 40 },
  /** 左右家手牌背面（§4.2，对家同档）。 */
  side: { w: 26, h: 40 },
  /** 自家副露（§4.4）。 */
  selfMeld: { w: 40, h: 56 },
  /** 他家副露（§4.1 / §4.2）。 */
  oppMeld: { w: 34, h: 46 },
} as const;

/**
 * 烘焙基准 60×84 → 各档显示的缩放（宽锚定，§6.1「同一绘制函数 + scale 复用」）。
 * 高度因烘焙纵横比略有近似（如侧边 26×36.4 vs 规格 26×40），属允许误差。
 */
export const TILE_SCALE = {
  hand: TILE_SPEC.hand.w / TILE_W,
  river: TILE_SPEC.river.w / TILE_W,
  side: TILE_SPEC.side.w / TILE_W,
  selfMeld: TILE_SPEC.selfMeld.w / TILE_W,
  oppMeld: TILE_SPEC.oppMeld.w / TILE_W,
} as const;

/* ─────────────────────────── 四家手牌区（§4） ─────────────────────────── */

/**
 * 自家手牌区（§4.6）：46×64，step=50 → **每张牌完全独立不遮挡**（牌间留 4px 空隙，适度紧凑）。
 *
 * 14 张总宽 = 46 + 13×50 = 696px，居中 x∈[292,988]，右侧留 12px 缓冲不碰按钮区（x≥1000）。
 */
export const SELF_HAND = {
  x: 640 - 696 / 2,
  /** 牌顶 y（牌底 y=664）。 */
  y: AREA.selfHand.y,
  /** 相邻牌步长（≥46 即零遮挡；50 留 4px 空隙，紧凑且不重叠）。 */
  step: 50,
  /** 选中态上浮 16px（§4.6 / §7.1）。 */
  lift: 16,
  /** 名牌行（自家）y。 */
  nameY: 580,
} as const;

/** 对家手牌区（§4.1）：26×40，竖排堆叠，fan 重叠 18 → 每张露出 22px；14 张总宽 296 居中 x∈[484,796]。 */
export const TOP_HAND = {
  x: 640 - (26 + 13 * 22) / 2,
  /** 牌顶 y∈[58,104]。 */
  y: 58,
  /** 相邻牌步长（= 重叠 18px，每张露 22px）。 */
  step: 22,
  /** 副露行 y∈[112,158]。 */
  meldY: 112,
  /** 副露横排居中 x。 */
  meldX: 640,
  /** 名牌行 y∈[166,192]。 */
  nameY: 166,
  /** 名牌行居中 x（区 x∈[430,850]）。 */
  nameX: 640,
} as const;

/** 左家手牌区（§4.2）：竖排，x∈[20,58]，垂直重叠 14 → 每张露出 26px；14 张 y∈[180,558]。 */
export const LEFT_HAND = {
  x: 20,
  /** 第一张牌顶 y。 */
  y: 180,
  /** 垂直步长（= 每张露出 26px）。 */
  step: 26,
  /** 副露区 x∈[66,140]。 */
  meldX: 66,
  /** 副露区首组 y∈[220,520]。 */
  meldY: 220,
  /** 名牌 x∈[16,150] 居中。 */
  nameX: 90,
  /** 名牌 y=600。 */
  nameY: 600,
} as const;

/** 右家手牌区（§4.2 镜像）：竖排，x∈[1222,1260]，y∈[180,558]。 */
export const RIGHT_HAND = {
  x: 1222,
  y: 180,
  step: 26,
  /** 副露区 x∈[1140,1214]。 */
  meldX: 1140,
  meldY: 220,
  /** 名牌 x∈[1130,1264] 居中。 */
  nameX: 1197,
  nameY: 600,
} as const;

/* ─────────────────────────── 中央弃牌河四边带（§4.3） ─────────────────────────── */

/**
 * 弃牌河四边带。每带为 `cols × rows` 网格：
 * 新牌优先填满当前行/列（行内从左到右），再换行；超容量时丢弃最旧一张并整体左移。
 * 索引 → `col = i % cols, row = floor(i / cols)`。
 */
export const RIVER = {
  /** 弃牌显示尺寸（30×40，§4.3）。 */
  tile: TILE_SPEC.river,
  /** 上带（对家弃牌）：x∈[438,842] y∈[252,340]，2 行（y=254,296）×10 列，列步长 40。 */
  top: { x0: 438, y0: 254, stepX: 40, stepY: 42, cols: 10, rows: 2, capacity: 20 },
  /** 下带（自家弃牌）：x∈[438,842] y∈[380,468]，2 行（y=382,424）×10 列，列步长 40。 */
  bottom: { x0: 438, y0: 382, stepX: 40, stepY: 42, cols: 10, rows: 2, capacity: 20 },
  /** 左带（左家弃牌）：x∈[304,430] y∈[252,506]，3 列（x=304,338,372）×6 行，行步长 42。 */
  left: { x0: 304, y0: 252, stepX: 34, stepY: 42, cols: 3, rows: 6, capacity: 18 },
  /** 右带（右家弃牌）：x∈[850,976] y∈[252,506]，镜像网格。 */
  right: { x0: 850, y0: 252, stepX: 34, stepY: 42, cols: 3, rows: 6, capacity: 18 },
} as const;

/** 中央信息区（§4.3）：x∈[440,840] y∈[340,380]，文字居中、字号 28。 */
export const CENTER_INFO = { x: 640, y: 360 } as const;

/* ─────────────────────────── 副露（§4.4 / §4.1 / §4.2） ─────────────────────────── */

/** 自家副露：x∈[896,1240] y∈[480,560] 横排；碰 3 张（40+2×26=92）、杠 4 张（40+3×20=100）；组间距 8。 */
export const SELF_MELD = {
  x: AREA.selfMeld.x,
  y: AREA.selfMeld.y,
  w: AREA.selfMeld.w,
  /** 组间距。 */
  gap: 8,
  /** 组内 tile 间距。 */
  tileGap: 2,
  /** 碰组内重叠步长（露出 26px）。 */
  pengStep: 26,
  /** 杠组内重叠步长（露出 20px）。 */
  gangStep: 20,
  /** 自适应缩放：极端 4 组总宽 > 344 时整体缩小。 */
  maxScale: 1,
} as const;

/** 对家副露：x∈[430,850] y=112 横排，牌 34×46，组间距 10。 */
export const TOP_MELD = {
  x: 640,
  y: 112,
  gap: 10,
} as const;

/** 左/右家副露：竖排，牌 34×46，组间距 8，y∈[220,520]。 */
export const SIDE_MELD = {
  gap: 8,
} as const;

/* ─────────────────────────── 操作按钮区（§4.5） ─────────────────────────── */

/**
 * 操作按钮区（右下角，右手拇指热区）：按钮 120×44、圆角 8。
 * step=50 时手牌右沿 x≈988，按钮区 x=1000 起，无冲突，回到美术规格原坐标。
 */
export const ACTION = {
  x: 1000,
  /** 碰 y=560 / 杠 y=608 / 胡 y=656。 */
  y0: 560,
  stepY: 48,
  w: 120,
  h: 44,
  radius: 8,
  /** PASS 与「胡」同行右侧（§4.5）。 */
  passX: 1144,
  passY: 656,
} as const;

/** 定缺花色卡（§7.3）：三张 120×80，手牌区上方中央。 */
export const VOID_CARDS = {
  x: BASE_W / 2,
  y: 500,
  w: 120,
  h: 80,
  gap: 20,
} as const;

/**
 * 牌墙剩余提示：**正中央偏下**（y=480，弃牌河下带与自家手牌之间空地，
 * 不挡对家手牌/弃牌河/phaseLabel）。2026-08-12 v2：用户报告 y=300 被对家手牌挡，下移。
 */
export const WALL_INDICATOR = { x: 640, y: 480 } as const;

/**
 * 胡牌翻牌位置（2026-08-12 用户要求）：在赢家面前放一张翻开的 winningTile
 * + 「胡」角标。每个座位一个固定锚点（多胡时同座位的牌叠放）。
 */
export const WIN_TILE_POS: Record<SeatIndex, { readonly x: number; readonly y: number }> = {
  // 自家：手牌中央上方（名牌与手牌之间）
  0: { x: 640, y: 555 },
  // 对家：副露下方、弃牌河上带之上
  2: { x: 640, y: 200 },
  // 右家：名牌行中央偏右
  1: { x: 1197, y: 560 },
  // 左家：名牌行中央偏左
  3: { x: 90, y: 560 },
};

/** 结算面板（§4.7）：(240,80) 尺寸 800×560，圆角 16。 */
export const SETTLE = {
  x: AREA.settle.x,
  y: AREA.settle.y,
  w: AREA.settle.w,
  h: AREA.settle.h,
  radius: 16,
  /** `--bg-panel` rgba(6,28,20,0.92)。 */
  fill: 0x061c14,
  fillAlpha: 0.92,
  /** `rgba(217,164,65,0.35)`。 */
  border: 0xd9a441,
  borderAlpha: 0.35,
} as const;

/* ─────────────────────────── 配色 Token（§5，Pixi 侧常量） ─────────────────────────── */

/** 桌布/面板色（§5 Token 表）。 */
export const TABLE_COLOR = {
  /** --bg-table-0 桌布最暗（径向渐变外圈）。 */
  bg0: 0x0a2c21,
  /** --bg-table-1 桌布主色。 */
  bg1: 0x0e3b2c,
  /** --bg-table-2 桌布中央亮区（聚焦）。 */
  bg2: 0x124530,
  /** --text-primary。 */
  textPrimary: 0xf2ede2,
  /** --text-secondary。 */
  textSecondary: 0x9fb3a8,
  /** --highlight 选中/高亮（亮金）。 */
  highlight: 0xf4c542,
  /** --warning。 */
  warning: 0xe05252,
  /** --score-pos 正分（金）。 */
  scorePos: 0xf0b23a,
  /** --score-neg 负分（蓝灰）。 */
  scoreNeg: 0x79a8d8,
  /** --btn-action 金。 */
  gold: 0xd9a441,
  /** 面板底色 --bg-panel。 */
  panel: 0x061c14,
  /** 面板描边。 */
  panelBorder: 0xd9a441,
} as const;

/** 操作按钮配色（§4.5 / §5）。 */
export const BUTTON_COLOR = {
  /** 碰/杠：--btn-action 金。 */
  action: 0xd9a441,
  /** 胡：--btn-hu 红。 */
  hu: 0xc0392b,
  /** PASS：--btn-pass 灰绿。 */
  pass: 0x5b6b63,
  /** 金按钮上的文字 --text-on-gold。 */
  onAction: 0x3a2a10,
  /** 胡按钮文字（白，对比 ≈6.5:1）。 */
  onHu: 0xffffff,
  /** PASS 文字 --text-primary。 */
  onPass: 0xf2ede2,
} as const;

/* ─────────────────────────── 字号 ─────────────────────────── */

/** 名牌行字号（§9 A2：昵称 ≥14）。 */
export const NAME_FONT = 14;
/** 中央信息区字号（§4.3：28）。 */
export const CENTER_FONT = 28;
